import { createFileRoute } from '@tanstack/react-router';
import { Button, Stack, Table, Text, TextInput } from '@mantine/core';
import { InlineLoader } from '@/shared/components/LoadingState';
import { IconSearch, IconReload, IconLanguage } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { fetchList } from '@/shared/api/jsonapi/client';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { MasterTenantNotice } from '@/features/tenants/MasterTenantNotice';
import { EmptyState } from '@/shared/components/EmptyState';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { mw, MW_CODE_STYLE } from '@/shared/mobile/tokens';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { MobileTranslations } from '@/features/translations/mobile/MobileTranslations';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { i18next } from '@/shared/i18n/config';
import { Paginated } from '@/shared/components/DataTable/TablePagination';

/** Contratto BE reale: un record per lingua con l'albero i18n serializzato in `translationWeb`. */
interface TranslationRow extends Denormalized {
  type: 'translations';
  id: string;
  languageCode?: string;
  translationWeb?: string;
}

export const Route = createFileRoute('/_app/translations')({ component: TranslationsRoute });

/** Selettore: traduzioni mobile (MaeDesign) vs tabella multi-lingua desktop. */
function TranslationsRoute() {
  const isMobile = useIsMobileShell();
  return isMobile ? <MobileTranslations /> : <TranslationsPage />;
}

/** Appiattisce l'albero JSON in coppie "a.b.c" → valore. */
function flatten(obj: unknown, prefix: string, out: Record<string, string>): Record<string, string> {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      const key = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === 'object' && !Array.isArray(v)) flatten(v, key, out);
      else out[key] = v == null ? '' : String(v);
    }
  }
  return out;
}

function TranslationsPage() {
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 250);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['translations'],
    staleTime: 5 * 60_000,
    queryFn: () => fetchList<TranslationRow>('translations', { page: { size: 50 } }),
  });

  const langs = useMemo(() => (data?.data ?? []).map((r) => r.languageCode ?? '—'), [data]);

  // Mappa lingua → { chiave piatta → valore }.
  const flatByLang = useMemo(() => {
    const m = new Map<string, Record<string, string>>();
    for (const r of data?.data ?? []) {
      const lang = r.languageCode ?? '—';
      let parsed: unknown = {};
      try { parsed = r.translationWeb ? JSON.parse(r.translationWeb) : {}; } catch { parsed = {}; }
      m.set(lang, flatten(parsed, '', {}));
    }
    return m;
  }, [data]);

  // Unione ordinata di tutte le chiavi.
  const allKeys = useMemo(() => {
    const s = new Set<string>();
    for (const map of flatByLang.values()) for (const k of Object.keys(map)) s.add(k);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [flatByLang]);

  const filteredKeys = useMemo(() => {
    const q = debounced.trim().toLowerCase();
    if (!q) return allKeys;
    return allKeys.filter((k) => {
      if (k.toLowerCase().includes(q)) return true;
      // cerca anche nei valori
      for (const map of flatByLang.values()) if ((map[k] ?? '').toLowerCase().includes(q)) return true;
      return false;
    });
  }, [allKeys, debounced, flatByLang]);


  return (
    <MaeShell>
      <MaePageHead
        icon={<IconLanguage size={22} />}
        tone="azzurro"
        title="Traduzioni"
        sub={`${allKeys.length} chiavi · lingue: ${langs.join(', ') || '—'}`}
        actions={
          <Button variant="light" leftSection={<IconReload size={16} />} onClick={() => void i18next.reloadResources()}>
            Ricarica nel client
          </Button>
        }
      />
      <Stack>
        <MasterTenantNotice cosa="Le traduzioni" />
        <TextInput
          placeholder="Cerca per chiave o valore (es. auth.login, Accedi)…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          w={{ base: '100%', sm: 420 }}
        />

        {isError ? (
          <QueryErrorAlert error={error} onRetry={() => void refetch()} />
        ) : isLoading ? (
          <div style={{ border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface, padding: 20 }}>
            <InlineLoader label="Caricamento delle traduzioni…" minHeight={140} />
          </div>
        ) : allKeys.length === 0 ? (
          <div style={{ border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface }}>
            <EmptyState icon={IconLanguage} title="Nessuna traduzione" description="Il sistema non ha restituito alberi di traduzione." py="md" />
          </div>
        ) : (
          <div style={{ border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface, overflow: 'hidden' }}>
            {/* Tabella paginata: c'era un cap di rendering a 300 chiavi (`VISIBLE`), quindi
                oltre la trecentesima le traduzioni non erano raggiungibili. */}
            <Paginated rows={filteredKeys} label="chiavi" barPad={16}>
            {(shown) => (
            <div style={{ overflowX: 'auto' }}>
              <Table verticalSpacing="xs" horizontalSpacing="md" highlightOnHover stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ minWidth: 260 }}>Chiave</Table.Th>
                    {langs.map((l) => <Table.Th key={l} style={{ minWidth: 220 }}>{l.toUpperCase()}</Table.Th>)}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {shown.map((k) => (
                    <Table.Tr key={k}>
                      <Table.Td><Text size="xs" style={MW_CODE_STYLE} c={mw.accentText}>{k}</Text></Table.Td>
                      {langs.map((l) => {
                        const v = flatByLang.get(l)?.[k];
                        return (
                          <Table.Td key={l}>
                            {v ? <Text size="sm" style={{ color: mw.ink900 }}>{v}</Text> : <Text size="sm" c="dimmed">—</Text>}
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
            )}
            </Paginated>
          </div>
        )}
      </Stack>
    </MaeShell>
  );
}
